import { action, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Check, EqualHeightProvider, Grid, GridColumn, Icon, ISelectorItemProps, Selector, Separator, Text } from 'telia-front-react';
import InternetCalculatorStore from '../../stores/InternetCalculatorStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { Segment } from '../../stores/SegmentStore';
import { InternetSuggestion, ProductCard } from './InternetCalculatorInterfaces';

interface SuggestInternetSpeedProps {
  calculatorKey: string;
  addressId: string;
  internetSpeeds: ProductCard[];
  b2bSuffix: string;
  segment?: Segment;
  suggestSpeed?: InternetSuggestion[];
  internetCalculatorStore?: InternetCalculatorStore;
}

@observer
class SuggestInternetSpeed extends React.Component<SuggestInternetSpeedProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  @observable accessor selectedDeviceCount: string = '1';
  @observable accessor selectedActivities: string[] = [];

  private activities: string[] = ['email', 'computer', 'folder', 'entertainment', 'games', 'camera'];

  @action
  private updateSelectedDeviceCount(count: string) {
    this.selectedDeviceCount = count;
    this.updateSuggestion();
  }

  @action
  private toggleActivity(checked: boolean, val: number) {
    if (checked) {
      this.selectedActivities.push(val.toString());
    } else {
      const index = this.selectedActivities.indexOf(val.toString());
      if (index !== -1) {
        this.selectedActivities.splice(index, 1);
      }
    }
    this.updateSuggestion();
  }

  @action
  private updateSuggestion() {
    const { calculatorKey, internetCalculatorStore } = this.props;
    if (this.selectedActivities.length === 0 && !this.selectedDeviceCount) {
      internetCalculatorStore.setInternetSuggestions(calculatorKey, null);
    } else {
      const suggestedOfferCodes: string[] = this.resolveSuggestedOfferCodes();
      internetCalculatorStore.setInternetSuggestions(calculatorKey, suggestedOfferCodes);
    }
  }

  private resolveSuggestedOfferCodes() {
    const { suggestSpeed, internetSpeeds } = this.props;
    const suggestedOfferCodes: string[] = [];
    const activeSuggestions = this.resolveSuggestions();
    internetSpeeds.map((internet) => {
      const suggestions = suggestSpeed.find((suggestion) => suggestion.offerCode === internet.productOfferingCode);
      if (suggestions && suggestions.suggestWhen && suggestions.suggestWhen.includes(activeSuggestions)) {
        suggestedOfferCodes.push(internet.productOfferingCode);
      }
    });
    if (!suggestedOfferCodes.length) {
      suggestedOfferCodes.push(this.findFirstProductOfferingCode());
    }
    return suggestedOfferCodes;
  }

  private findFirstProductOfferingCode(): string {
    const { internetSpeeds } = this.props;
    return internetSpeeds[0].productOfferingCode;
  }

  private resolveSuggestions(): string {
    let result = '';
    if (this.selectedDeviceCount) {
      result += `c${this.selectedDeviceCount}`;
    }
    this.selectedActivities.sort().map((activity) => (result += `w${activity}`));
    return result;
  }

  private getDeviceCountChoiceId(index: number | string): string {
    return `internetAndTvCalculator_deviceCount-${index}`;
  }

  private getDeviceCountChoices(): ISelectorItemProps[] {
    const translate = this.context.translate;
    const { b2bSuffix } = this.props;
    const deviceChoices: ISelectorItemProps[] = [];
    for (let index = 1; index < 5; index++) {
      deviceChoices.push({
        id: this.getDeviceCountChoiceId(index),
        name: `internetAndTvCalculator_deviceCount${b2bSuffix}`,
        label: translate(`internetAndTvCalculator.deviceCount${index}${b2bSuffix}`),
        value: `${index}`
      });
    }
    return deviceChoices;
  }

  private getActivityId(activity: string): string {
    return `device-check-${activity}`;
  }

  private renderActivities() {
    const translate = this.context.translate;
    return this.activities.map((activity, index) => {
      const counter = index + 1;
      return (
        <GridColumn key={counter} width={['xs']}>
          <Check
            labelText={translate(`internetAndTvCalculator.suggest.label.activity-${activity}`)}
            id={this.getActivityId(activity)}
            value={counter.toString()}
            direction="vertical"
            children={
              <Text>
                <Icon modifiers={`#${activity}`} size="lg" />
                <p>{translate(`internetAndTvCalculator.suggest.activity-${activity}`)}</p>
              </Text>
            }
            checked={this.selectedActivities.includes(counter.toString())}
            onChange={(checked) => this.toggleActivity(checked, counter)}
          />
        </GridColumn>
      );
    });
  }

  public render() {
    const translate = this.context.translate;
    const { segment, b2bSuffix } = this.props;

    return (
      <>
        <Grid align={['center-xs']}>
          <GridColumn width={['xs']}>
            <Text className="center">
              <p className="text-bold">{translate(`internetAndTvCalculator.suggest.device.title${b2bSuffix}`)}</p>
              <Selector
                id={'suggest-internet-speed-selector'}
                label=""
                items={this.getDeviceCountChoices()}
                onChange={(value: string | string[]) => this.updateSelectedDeviceCount(value as string)}
                value={this.selectedDeviceCount || ''}
              />
            </Text>
          </GridColumn>
        </Grid>
        {segment !== 'BUSINESS' && (
          <>
            <Separator spacing="small" color="invisible" />
            <EqualHeightProvider groups={['internetAndTvCalculator-suggest-activities']}>
              <Text className="text-center">
                <p className="text-bold">{translate('internetAndTvCalculator.suggest.activity.title')}</p>
                <Grid align={['center-xs']} equalHeight={true}>
                  {this.renderActivities()}
                </Grid>
              </Text>
            </EqualHeightProvider>
          </>
        )}
      </>
    );
  }
}

export default inject('internetCalculatorStore')(SuggestInternetSpeed);
